import React, { useEffect, useState } from 'react';
import { authService } from '../services/authService';
import { growerService } from '../services/growerService';
import { locationService } from '../services/locationService';
import { sensorService } from '../services/sensorService';
import Layout from '../components/Layout';
import { User, Grower, Location, LocationCreate, LocationUpdate, DeleteLocationRequest, Sensor } from '../types';

const Locations: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [growers, setGrowers] = useState<Grower[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [allSensors, setAllSensors] = useState<Sensor[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');

  // Create/edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState<LocationCreate>({ name: '', description: '', grower_id: 0 });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState<Location | null>(null);
  const [deleteRequest, setDeleteRequest] = useState<DeleteLocationRequest>({ sensor_action: 'unassign' });
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setPageError('');
      const [currentUser, growersData, locationsData, sensorsData] = await Promise.all([
        authService.getCurrentUser(),
        growerService.getGrowers(),
        locationService.getLocations(),
        sensorService.getSensors().catch(() => [] as Sensor[]),
      ]);
      setUser(currentUser);
      setGrowers(growersData);
      setLocations(locationsData);
      setAllSensors(sensorsData);
    } catch (err: any) {
      setPageError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  // ── Create ────────────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingLocation(null);
    const defaultGrowerId = growers.length > 0 ? growers[0].id : 0;
    setFormData({ name: '', description: '', grower_id: defaultGrowerId });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (loc: Location) => {
    setEditingLocation(loc);
    setFormData({ name: loc.name, description: loc.description || '', grower_id: loc.grower_id });
    setFormError('');
    setSelectedSensorIds(new Set());
    setShowModal(true);
  };

  // Multi-select state for sensor assignment
  const [selectedSensorIds, setSelectedSensorIds] = useState<Set<number>>(new Set());
  const [assigningSensors, setAssigningSensors] = useState(false);

  const refreshLocationData = async (locationId: number) => {
    const [locationsData, sensorsData] = await Promise.all([
      locationService.getLocations(),
      sensorService.getSensors().catch(() => [] as Sensor[]),
    ]);
    setLocations(locationsData);
    setAllSensors(sensorsData);
    const updated = locationsData.find((l) => l.id === locationId);
    if (updated) setEditingLocation(updated);
  };

  const handleAssignSelectedSensors = async () => {
    if (!editingLocation || selectedSensorIds.size === 0) return;
    setAssigningSensors(true);
    setFormError('');
    try {
      await Promise.all(
        Array.from(selectedSensorIds).map((id) =>
          locationService.assignSensorLocation(id, { location_id: editingLocation.id })
        )
      );
      setSelectedSensorIds(new Set());
      await refreshLocationData(editingLocation.id);
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to assign sensors');
    } finally {
      setAssigningSensors(false);
    }
  };

  const handleUnassignSensor = async (sensorId: number) => {
    if (!editingLocation) return;
    try {
      await locationService.assignSensorLocation(sensorId, { location_id: null });
      await refreshLocationData(editingLocation.id);
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to unassign sensor');
    }
  };

  const toggleSensorSelection = (id: number) => {
    setSelectedSensorIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (!formData.grower_id) {
      setFormError('Please select a grower');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      let savedLocationId: number | null = null;

      if (editingLocation) {
        await locationService.updateLocation(editingLocation.id, {
          name: formData.name,
          description: formData.description || undefined,
        } as LocationUpdate);
        savedLocationId = editingLocation.id;
      } else {
        const created = await locationService.createLocation(formData);
        savedLocationId = created.id;
      }

      // Assign any sensors that are checked in the list
      if (savedLocationId !== null && selectedSensorIds.size > 0) {
        await Promise.all(
          Array.from(selectedSensorIds).map((id) =>
            locationService.assignSensorLocation(id, { location_id: savedLocationId })
          )
        );
        setSelectedSensorIds(new Set());
      }

      // Full refresh so the locations list reflects all changes
      await loadData();
      setShowModal(false);
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const openDeleteModal = (loc: Location) => {
    setDeletingLocation(loc);
    setDeleteRequest({ sensor_action: 'unassign', target_location_id: null });
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deletingLocation) return;
    if (deleteRequest.sensor_action === 'reassign' && !deleteRequest.target_location_id) {
      setDeleteError('Please select a target location');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await locationService.deleteLocation(deletingLocation.id, deleteRequest);
      setLocations(locations.filter((l) => l.id !== deletingLocation.id));
      setShowDeleteModal(false);
    } catch (err: any) {
      setDeleteError(err.response?.data?.detail || 'Failed to delete location');
    } finally {
      setDeleting(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getGrowerName = (growerId: number) =>
    growers.find((g) => g.id === growerId)?.name || `Grower #${growerId}`;

  // Group locations by grower
  const locationsByGrower: { [growerId: number]: Location[] } = {};
  for (const loc of locations) {
    if (!locationsByGrower[loc.grower_id]) locationsByGrower[loc.grower_id] = [];
    locationsByGrower[loc.grower_id].push(loc);
  }

  const canManage = user?.is_admin || user?.is_admin_grower || false;

  // Other locations in the same grower (for reassign target dropdown)
  const reassignTargetOptions =
    deletingLocation
      ? locations.filter((l) => l.grower_id === deletingLocation.grower_id && l.id !== deletingLocation.id)
      : [];

  if (loading) {
    return (
      <Layout user={user ? { email: user.email, full_name: user.full_name, is_admin: user.is_admin, is_admin_grower: user.is_admin_grower } : undefined}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user ? { email: user.email, full_name: user.full_name, is_admin: user.is_admin, is_admin_grower: user.is_admin_grower } : undefined}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
          {(user?.is_admin || user?.is_admin_grower) && (
            <button
              onClick={openCreateModal}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium"
            >
              Add Location
            </button>
          )}
        </div>

        {pageError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{pageError}</div>
        )}

        {/* Locations grouped by grower */}
        {Object.keys(locationsByGrower).length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            <p>No locations found.</p>
            {canManage && (
              <button
                onClick={openCreateModal}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium"
              >
                Add your first location
              </button>
            )}
          </div>
        ) : (
          Object.entries(locationsByGrower).map(([growerId, locs]) => (
            <div key={growerId} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {getGrowerName(Number(growerId))}
                </h2>
              </div>
              <div className="divide-y divide-gray-100">
                {locs.map((loc) => (
                  <div key={loc.id} className="px-6 py-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-medium text-gray-900">{loc.name}</h3>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {loc.sensors.length} sensor{loc.sensors.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {loc.description && (
                        <p className="mt-1 text-sm text-gray-500">{loc.description}</p>
                      )}
                      {loc.sensors.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {loc.sensors.map((s) => (
                            <span
                              key={s.id}
                              className={`text-xs px-2 py-0.5 rounded-full border ${
                                s.is_active
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : 'bg-gray-50 text-gray-500 border-gray-200'
                              }`}
                            >
                              {s.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => openEditModal(loc)}
                          className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openDeleteModal(loc)}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingLocation ? 'Edit Location' : 'Add Location'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{formError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. Rose field, Spinach greenhouse"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={2}
                  placeholder="Optional description"
                />
              </div>
              {!editingLocation && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grower *</label>
                  <select
                    value={formData.grower_id}
                    onChange={(e) => setFormData({ ...formData, grower_id: Number(e.target.value) })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={0} disabled>Select a grower</option>
                    {growers.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Sensor assignment — only when editing an existing location */}
              {editingLocation && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sensors</label>

                  {/* Assigned sensors */}
                  {editingLocation.sensors.length > 0 && (
                    <div className="mb-3 space-y-1">
                      {editingLocation.sensors.map((s) => (
                        <div key={s.id} className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-3 py-1.5 text-sm">
                          <span className="text-green-800 font-medium">{s.name}</span>
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => handleUnassignSensor(s.id)}
                              className="text-red-500 hover:text-red-700 text-xs ml-2 shrink-0"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Multi-select available sensors */}
                  {canManage && (() => {
                    const assignedIds = new Set(editingLocation.sensors.map((s) => s.id));
                    const available = allSensors.filter(
                      (s) => s.grower_id === editingLocation.grower_id && !assignedIds.has(s.id)
                    );
                    if (available.length === 0) {
                      return (
                        <p className="text-xs text-gray-400 italic">
                          {allSensors.filter((s) => s.grower_id === editingLocation.grower_id).length === 0
                            ? 'No sensors belong to this grower yet.'
                            : 'All sensors for this grower are already assigned here.'}
                        </p>
                      );
                    }
                    const allSelected = available.every((s) => selectedSensorIds.has(s.id));
                    return (
                      <div className="border border-gray-200 rounded-md overflow-hidden">
                        {/* Select all header */}
                        <div className="flex items-center justify-between bg-gray-50 px-3 py-2 border-b border-gray-200">
                          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={() => {
                                if (allSelected) {
                                  setSelectedSensorIds(new Set());
                                } else {
                                  setSelectedSensorIds(new Set(available.map((s) => s.id)));
                                }
                              }}
                              className="rounded text-primary-600"
                            />
                            Select all ({available.length})
                          </label>
                          <button
                            type="button"
                            onClick={handleAssignSelectedSensors}
                            disabled={selectedSensorIds.size === 0 || assigningSensors}
                            className="px-3 py-1 text-xs font-medium bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {assigningSensors ? 'Adding…' : `Add selected (${selectedSensorIds.size})`}
                          </button>
                        </div>
                        {/* Sensor list */}
                        <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                          {available.map((s) => (
                            <label key={s.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedSensorIds.has(s.id)}
                                onChange={() => toggleSensorSelection(s.id)}
                                className="rounded text-primary-600"
                              />
                              <span className="text-sm text-gray-800 flex-1">{s.name}</span>
                              <span className="text-xs text-gray-400 font-mono">{s.device_id}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {saving
                  ? 'Saving...'
                  : selectedSensorIds.size > 0
                  ? `Save & assign ${selectedSensorIds.size} sensor${selectedSensorIds.size !== 1 ? 's' : ''}`
                  : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && deletingLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Delete Location</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              {deleteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{deleteError}</div>
              )}
              <p className="text-sm text-gray-700">
                Are you sure you want to delete location <strong>"{deletingLocation.name}"</strong>?
              </p>
              {deletingLocation.sensors.length > 0 && (
                <>
                  <p className="text-sm text-gray-600">
                    This location has <strong>{deletingLocation.sensors.length}</strong> sensor{deletingLocation.sensors.length !== 1 ? 's' : ''} assigned to it. What should happen to them?
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="sensorAction"
                        value="unassign"
                        checked={deleteRequest.sensor_action === 'unassign'}
                        onChange={() => setDeleteRequest({ sensor_action: 'unassign', target_location_id: null })}
                        className="text-primary-600"
                      />
                      <span className="text-sm text-gray-700">Unassign sensors (remove from location)</span>
                    </label>
                    {reassignTargetOptions.length > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="sensorAction"
                          value="reassign"
                          checked={deleteRequest.sensor_action === 'reassign'}
                          onChange={() => setDeleteRequest({ sensor_action: 'reassign', target_location_id: reassignTargetOptions[0].id })}
                          className="text-primary-600"
                        />
                        <span className="text-sm text-gray-700">Move sensors to another location</span>
                      </label>
                    )}
                  </div>
                  {deleteRequest.sensor_action === 'reassign' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target location</label>
                      <select
                        value={deleteRequest.target_location_id || ''}
                        onChange={(e) => setDeleteRequest({ ...deleteRequest, target_location_id: Number(e.target.value) })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="" disabled>Select a location</option>
                        {reassignTargetOptions.map((l) => (
                          <option key={l.id} value={l.id}>{l.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Locations;
